import PropTypes from 'prop-types';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
// eslint-disable-next-line import/no-unresolved
import { styled, useTheme } from '@mui/material/styles';
// _mock
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
// eslint-disable-next-line import/no-unresolved
import Label from 'src/components/Label';
import AxiosAll from 'src/services/axiosAll';
import { FormProvider, RHFSelect, RHFTextField } from '../../components/hook-form';

// ----------------------------------------------------------------------

const LabelStyle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

CreateTaskPriorities.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  currentPriority: PropTypes.object,
  isEdit: PropTypes.bool,
  getAllPriorities: PropTypes.func,
};

export default function CreateTaskPriorities({ open, onClose, isEdit, currentPriority, getAllPriorities }) {
  const theme = useTheme();
  const { userDetails } = useSelector((state) => state.loginReducer);

  const { enqueueSnackbar } = useSnackbar();

  const NewPrioritySchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    description: Yup.string().required('Description is required'),
    color: Yup.string().required('Color is required'),
  });

  const defaultValues = useMemo(
    () => ({
      name: currentPriority?.name || '',
      description: currentPriority?.description || '',
      color: currentPriority?.color || '',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPriority]
  );

  const methods = useForm({
    resolver: yupResolver(NewPrioritySchema),
    defaultValues,
  });

  const {
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  useEffect(() => {
    if (isEdit && currentPriority) {
      reset(defaultValues);
    } else {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentPriority]);

  const onSubmit = async () => {
    try {
      const formData = new FormData();
      formData.append('name', values?.name);
      formData.append('color', values?.color);
      formData.append('description', values?.description);

      const response = await AxiosAll(
        'post',
        isEdit ? `/task-priorities/${currentPriority?.id}` : '/task-priorities',
        formData,
        userDetails.token
      );

      const { data } = response;

      if (data.statusCode === 200) {
        enqueueSnackbar(data?.message, { variant: 'success' });
        onClose();
        getAllPriorities();
        reset();
      } else {
        enqueueSnackbar(data?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      // onClose={() => {
      //   onClose();
      //   reset(defaultValues);
      // }}
    >
      <DialogTitle>Add New Task Priority</DialogTitle>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3}>
            <div>
              <LabelStyle>Name</LabelStyle>
              <RHFTextField name="name" placeholder="Task Priority Name" />
            </div>
            <div>
              <LabelStyle>Description</LabelStyle>
              <RHFTextField name="description" placeholder="Task Priority Description..." multiline rows={4} />
            </div>
            {values?.color && (
              <div>
                <LabelStyle>Preview</LabelStyle>
                <Label color={values?.color || 'default'} sx={{ width: '100%', height: 48, borderRadius: 2 }}>
                  {values?.name || 'sample text'}
                </Label>
              </div>
            )}
            <div>
              <LabelStyle>Color</LabelStyle>
              <RHFSelect name="color" placeholder="Choose color">
                <option value="">Select</option>
                <option value="default">Default</option>
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="info">Info</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
              </RHFSelect>
            </div>
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {isEdit ? 'Update' : 'Create'}
          </LoadingButton>
          <Button
            color="inherit"
            variant="outlined"
            onClick={() => {
              onClose();
              reset(defaultValues);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
